import { GoogleOAuthProvider } from '@react-oauth/google'
import { consumeConfirmResume } from 'utils/shopifyConfirmResume'
import * as Sentry from '@sentry/react'
import { scrubBreadcrumb, scrubEvent } from 'utils/sentryScrub'
import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import TagManager from 'react-gtm-module'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import * as rudderanalytics from 'rudder-sdk-js'

import ScreenLoading from 'components/ScreenLoading'
import './CustomBootstrap.scss'
import './css/Tooltip.scss'

import useToken from './containers/authentication/useToken'

import './css/Forms.scss'
import './css/Header.scss'
import './css/Layout.scss'
import './css/Common.scss'
import './css/Sidebar.scss'
import './css/Typography.scss'

import ForgotPassword from 'pages/ForgotPassword/ForgotPassword'
import ResetPassword from 'pages/ForgotPassword/ResetPassword'
import SignIn from './pages/SignIn/SignIn'
import CreateOrganization from './pages/SignUp/CreateOrganization'
import OrganizationSelect from './pages/OrganizationSelect/OrganizationSelect'
import SignUp from './pages/SignUp/SignUp'
import MergeAccount from './pages/SignIn/MergeAccount'
import VerifyEmail from 'pages/SignIn/VerifyEmail'
import DatasetSelect from './pages/DatasetSelect/DatasetSelect'

import Home from './pages/Home/Home'
import LandingAddAccounts from './pages/Sources/LandingAddAccounts'

import ProtectedRoute from './components/ProtectedRoute'
import {
  selectToken,
  setToken,
} from './containers/authentication/authenticationSlice'

import AcceptInvite from 'pages/Invites/AcceptInvite'

import OrganizationRoute from 'components/OrganizationRoute'
import Page404 from 'pages/ErrorPages/404'

import CheckoutCancel from './pages/Billing/CheckoutCancel'
import CheckoutSuccess from './pages/Billing/CheckoutSuccess'

import ConfirmationMessage from 'components/ConfirmationMessage'
import ConfirmationContextProvider from 'containers/confirmation/confirmationContextProvider'
import LatestOrganizationRoute from 'components/LatestOrganizationRoute'

import api from './containers/api'
import GooglePlayOauth from 'components/Sources/GooglePlayOauth'
import MicrosoftRedirect from 'pages/SignIn/MicrosoftRedirect'
import GoogleRedirect from 'pages/SignIn/GoogleRedirect'
import MonaStart from 'pages/Mona/MonaStart'
import MonaRedirect from 'pages/Mona/MonaRedirect'
import PopUpMessage from 'components/PopUpMessage'
import ShopifyIntegration from 'pages/ShopifyIntegration'
import { captureSignupAttribution } from 'utils/signupAttribution'

if (window.location.hostname === 'app.mayainsights.com') {
  Sentry.init({
    dsn: 'https://4583be6c8d584e848ec9684dc93865c0@o475428.ingest.sentry.io/5718408',
    // Page URLs carry mcp_auth_token and OAuth codes; see utils/sentryScrub.ts.
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  })
}
// Where a visitor came from (UTMs, intent, shop), kept until signup (#1819, #1820).
captureSignupAttribution()

const App = () => {
  const dispatch = useDispatch()
  const { isLoadingToken } = useToken()
  const token = useSelector(selectToken)
  const [cookies] = useCookies(['user_id'])
  const [redirectError, setRedirectError] = useState((new URLSearchParams(window.location.search)).get('redirect_error'))

  // Failed return leg of the subscription-confirm re-auth hop (#1658): /oauth/redirect
  // error-lands on '/?redirect_error=…', dropping the merchant out of the activation UX
  // even though their subscription is active. If a fresh confirm-resume stash exists,
  // route back to the confirm page — the once-per-charge marker suppresses a second hop,
  // so the flow continues on the stored credentials (pre-hop status quo).
  useEffect(() => {
    if (!redirectError) return
    const resume = consumeConfirmResume()
    if (resume) window.location.replace(resume)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      token &&
      (!cookies.user_id || parseInt(cookies.user_id) !== token.userId)
    ) {
      dispatch(setToken(null))
    }
    // eslint-disable-next-line
  }, [cookies])

  const loadGtm = async () => {
    const gtmId = await api.getGoogleTagManagerId()
    if (gtmId) {
      TagManager.initialize({ gtmId })
    }
  }

  const loadRudderStack = async () => {
    const rudderConf = await api.getRudderstackConfiguration()

    rudderanalytics.ready(() => console.log('rudderstack ready'))

    if (rudderConf) {
      rudderanalytics.load(rudderConf.write_key, rudderConf.data_plane_url)
    } else {
      console.log('no rudderstack configuration found')
    }
  }

  const handleRedirectErrorMessage = () => {
    switch (redirectError) {
      case 'no_such_user':
        return "We couldn't find a user with that email address. Please create an account."
      case 'subscription_does_not_exist':
        return 'This subscription does not exist. Please contact support.'
      // Shopify OAuth callback failures (api/routers/shopify_billing.py). We land back on
      // the app and (with the stored Shopify params) resume the connect from Home.
      case 'oauth_invalid_hmac':
      case 'oauth_invalid_params':
      case 'oauth_invalid_state':
        return "We couldn't verify the response from Shopify. Please try connecting your store again."
      case 'oauth_missing_organization':
      case 'oauth_invalid_organization':
        return 'We lost track of which organization to connect. Please start the Shopify connection again.'
      case 'oauth_failed':
        return 'Something went wrong connecting your Shopify store. Please try again, or contact us if it keeps happening.'
      default:
        return 'An unknown error occurred. Please try again later.'
    }
  }

  useEffect(() => {
    loadGtm()
    loadRudderStack()
  }, [])

  // A screen without the chat loaded (e.g. the Shopify connect error card) can request
  // support by setting this flag and navigating here. The HubSpot widget is GTM-injected on
  // the app shell, so open it once it's ready.
  useEffect(() => {
    if (sessionStorage.getItem('open_support_chat') !== '1') return
    sessionStorage.removeItem('open_support_chat')
    const w = window as any
    const openChat = () => w.HubSpotConversations?.widget?.open?.()
    if (w.HubSpotConversations?.widget) {
      openChat()
    } else {
      w.hsConversationsOnReady = Array.isArray(w.hsConversationsOnReady) ? w.hsConversationsOnReady : []
      w.hsConversationsOnReady.push(openChat)
    }
  }, [])

  if (isLoadingToken) {
    return <ScreenLoading />
  }

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '1017969023466-rndtdtnqk73rujtlk24r1ntbmer4rsjt.apps.googleusercontent.com'

  return (
    <Sentry.ErrorBoundary fallback={'An error has occured'}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ConfirmationContextProvider>
      {redirectError && 
        <PopUpMessage
          time={5000}
          onClose={() => setRedirectError(null)}
          type="error"
          message={handleRedirectErrorMessage()}
          styles={{top: "10%"}}
        />}
        <ConfirmationMessage />
        <BrowserRouter>
          <Switch>
            <Route exact path="/sign-in" component={SignIn} />
            <Route exact path="/organization-select" component={OrganizationSelect} />
            <Route exact path="/dataset-select" component={DatasetSelect} />
            <Route exact path="/merge-account" component={MergeAccount} />
            <Route exact path="/verify-email" component={VerifyEmail} />
            <Route path="/sign-up" component={SignUp} />
            <Route exact path="/forgot-password" component={ForgotPassword} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route exact path="/microsoft/redirect" component={MicrosoftRedirect} />
            <Route exact path="/google/redirect" component={GoogleRedirect} />
            <Route exact path="/microsoft/mona/start" component={MonaStart} />
            <Route exact path="/microsoft/mona/redirect" component={MonaRedirect} />
            <Route exact path="/shopify-integration" component={ShopifyIntegration} />
            <Route
              exact
              path="/invitation/:organizationSlug/:inviteId"
              component={AcceptInvite}
            />

            <ProtectedRoute
              exact
              path="/create-organization"
              userId={parseInt(cookies.user_id)}
              token={token}
              component={CreateOrganization}
            />

            <ProtectedRoute
              exact
              path="/"
              userId={parseInt(cookies.user_id)}
              token={token}
              component={Home}
            />
            <ProtectedRoute
              path="/integrations/:serviceId/oauth/redirect"
              userId={parseInt(cookies.user_id)}
              token={token}
              component={LandingAddAccounts}
            />

            <Route
              path="/sources/setup/google_play/oauth"
              component={GooglePlayOauth}
            />

            <ProtectedRoute
              path="/organization/0"
              userId={parseInt(cookies.user_id)}
              token={token}
              component={LatestOrganizationRoute}
            />

            <ProtectedRoute
              path="/organization/:organizationSlug"
              userId={parseInt(cookies.user_id)}
              token={token}
              component={OrganizationRoute}
            />

            <ProtectedRoute
              exact
              path={`/checkout-success`}
              userId={parseInt(cookies.user_id)}
              token={token}
              component={CheckoutSuccess}
            />
            <ProtectedRoute
              exact
              path={`/checkout-cancel`}
              userId={parseInt(cookies.user_id)}
              token={token}
              component={CheckoutCancel}
            />
            <Route component={() => <Page404 />} />
          </Switch>
        </BrowserRouter>
      </ConfirmationContextProvider>
      </GoogleOAuthProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
